import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchListings } from "../api/marketingApi";
import { ListingCard } from "../components/ListingCard";
import { useShortlist } from "../hooks/useShortlist";

const TYPES = ["apartment", "house", "villa", "plot", "commercial"];
const STATES = [
  ["new", "New"],
  ["rental", "For Rent"],
  ["resale", "Resale"],
];

export function MarketingProperties() {
  const [params, setParams] = useSearchParams();
  const savedView = params.get("saved") === "1";
  const filters = {
    type: params.get("type") || "",
    state: params.get("state") || "",
    city: params.get("city") || "",
  };
  const { tokens } = useShortlist();

  const [data, setData] = useState({ listings: [], cities: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchListings(filters)
      .then((d) => alive && setData(d))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.type, filters.state, filters.city]);

  const shown = useMemo(() => {
    if (!savedView) return data.listings;
    const set = new Set(tokens);
    return data.listings.filter((l) => set.has(l.token));
  }, [data.listings, savedView, tokens]);

  function setFilter(key, value) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete("saved");
    setParams(next);
  }

  const heading = savedView
    ? "Your shortlist"
    : filters.city
      ? `Properties in ${filters.city}`
      : "Properties";

  return (
    <section className="mx-auto max-w-[74rem] px-6 pb-16 pt-10">
      <span className="font-sans text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-[#B7975A]">Browse</span>
      <h1 className="mb-6 mt-1 text-[clamp(1.8rem,4vw,2.6rem)] font-semibold tracking-tight">{heading}</h1>

      {!savedView && (
        <div className="mb-8 space-y-4">
          <FilterRow label="Type" options={[["", "All types"], ...TYPES.map((t) => [t, cap(t)])]} value={filters.type} onSelect={(v) => setFilter("type", v)} />
          <FilterRow label="Offer" options={[["", "All"], ...STATES]} value={filters.state} onSelect={(v) => setFilter("state", v)} />
          {data.cities.length > 0 && (
            <FilterRow label="City" options={[["", "All cities"], ...data.cities.map((c) => [c, c])]} value={filters.city} onSelect={(v) => setFilter("city", v)} />
          )}
        </div>
      )}

      <div className="mb-5 font-sans text-[0.88rem] text-[#78746A]">
        {loading ? "Loading…" : `${shown.length} ${shown.length === 1 ? "property" : "properties"}`}
      </div>

      {!loading && shown.length === 0 ? (
        <div className="py-16 text-center font-sans text-[#78746A]">
          {savedView
            ? "Your shortlist is empty. Tap the heart on a property to save it here."
            : "No properties match these filters right now."}
        </div>
      ) : (
        <div className="grid gap-6 [grid-template-columns:repeat(auto-fill,minmax(280px,1fr))]">
          {shown.map((l) => (
            <ListingCard key={l.token} listing={l} />
          ))}
        </div>
      )}
    </section>
  );
}

function FilterRow({ label, options, value, onSelect }) {
  return (
    <div>
      <div className="mb-2 font-sans text-[0.7rem] uppercase tracking-[0.1em] text-[#78746A]">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map(([val, lbl]) => {
          const active = String(value) === String(val);
          return (
            <button
              key={val}
              type="button"
              onClick={() => onSelect(val)}
              className={`rounded-full border px-[0.9rem] py-[0.45rem] font-sans text-[0.85rem] font-medium transition ${
                active
                  ? "border-[#2C4A87] bg-[#2C4A87] text-white"
                  : "border-[#C9C3B4] bg-[#FBFAF6] text-[#4A473E] hover:border-[#78746A]"
              }`}
            >
              {lbl}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function cap(s) {
  return String(s).charAt(0).toUpperCase() + String(s).slice(1);
}
